/**
 * Validates IEstimateFeesRequest for RPC-based fee estimation
 *
 * @param request - The fee estimation request to validate
 * @throws {Error} When required fields are missing or invalid
 */
export function validateIEstimateFeesRequest(request) {
    validateRequiredFields(request);
    validateChainId(request.chainId);
    validateAddresses(request.transaction.to, request.transaction.from);
    validateOptionalValue(request.transaction.value);
    validateOptionalData(request.transaction.data);
}
function validateRequiredFields(request) {
    if (!request.transaction) {
        throw new Error("validation failed: missing transaction field");
    }
    if (!request.transaction.to) {
        throw new Error("validation failed: missing transaction.to field");
    }
    if (!request.transaction.from) {
        throw new Error("validation failed: missing transaction.from field (required for RPC estimation)");
    }
    if (!request.chainId) {
        throw new Error("validation failed: chainId is required");
    }
}
function validateChainId(chainId) {
    if (typeof chainId !== "string") {
        throw new Error("validation failed: chainId must be a string");
    }
}
function validateAddresses(to, from) {
    if (!isValidAddress(to)) {
        throw new Error("validation failed: invalid destination address format");
    }
    if (!isValidAddress(from)) {
        throw new Error("validation failed: invalid source address format");
    }
}
function validateOptionalValue(value) {
    if (value === undefined)
        return;
    if (typeof value !== "string") {
        throw new Error("validation failed: transaction.value must be a string");
    }
    try {
        BigInt(value);
    }
    catch {
        throw new Error("validation failed: transaction.value must be a valid number string");
    }
}
function validateOptionalData(data) {
    if (data === undefined)
        return;
    if (typeof data !== "string") {
        throw new Error("validation failed: transaction.data must be a string");
    }
    if (data && !data.startsWith("0x")) {
        throw new Error("validation failed: transaction.data must start with 0x");
    }
}
/**
 * Basic address format validation
 * @param address - Address to validate
 * @returns True if address format is valid
 */
function isValidAddress(address) {
    // Basic Ethereum address validation (0x followed by 40 hex characters)
    return /^0x[a-fA-F0-9]{40}$/.test(address);
}
/**
 * Validates an IEstimateFeesRequest for Solana RPC-based fee estimation.
 *
 * @remarks
 * Unlike the EVM validator, this requires:
 * - Base58 addresses for `to` and `from`
 * - A base64-encoded serialized transaction in `data` (required for `getFeeForMessage`)
 *
 * @param request - The fee estimation request to validate
 * @param chainService - Used for Solana address format validation
 * @throws {Error} When required fields are missing or have invalid format
 */
export function validateSolanaFeeRequest(request, chainService) {
    if (!request.transaction) {
        throw new Error("validation failed: missing transaction field");
    }
    if (!request.chainId) {
        throw new Error("validation failed: chainId is required");
    }
    if (typeof request.chainId !== "string") {
        throw new Error("validation failed: chainId must be a string");
    }
    if (!request.transaction.to) {
        throw new Error("validation failed: missing transaction.to field");
    }
    if (!request.transaction.from) {
        throw new Error("validation failed: missing transaction.from field (required for RPC estimation)");
    }
    if (!chainService.isValidSolanaAddress(request.transaction.to)) {
        throw new Error("validation failed: invalid destination address format (expected Solana base58)");
    }
    if (!chainService.isValidSolanaAddress(request.transaction.from)) {
        throw new Error("validation failed: invalid source address format (expected Solana base58)");
    }
    if (!request.transaction.data) {
        throw new Error("validation failed: transaction.data is required for Solana fee estimation " +
            "(pass the base64-encoded transaction from buildStakeTx)");
    }
    if (request.transaction.data.startsWith("0x")) {
        throw new Error("validation failed: transaction.data must be base64-encoded for Solana (not 0x-prefixed hex)");
    }
}
export function validateIGetGasPriceRequest(request) {
    // chainId and network are optional, so no validation needed for basic request
    // Could add format validation for chainId if needed
    if (request.chainId && typeof request.chainId !== "string") {
        throw new Error("chainId must be a string");
    }
    if (request.network && typeof request.network !== "string") {
        throw new Error("network must be a string");
    }
}
//# sourceMappingURL=data:application/json;base64,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